import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  useCloseReasons,
  useCreateCloseReason,
  useUpdateCloseReason,
  useDeleteCloseReason,
} from '@/hooks/useCloseReasons';
import { Button, Card, CardBody, CardHeader, Input, Modal, Select, Skeleton, Textarea } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { CloseReason, CloseReasonCategory } from '@/types';

const categoryLabels: Record<CloseReasonCategory, string> = {
  NO_RESPONSE: 'No Response',
  NOT_A_FIT: 'Not a Fit',
  BUDGET: 'Budget',
  TIMING: 'Timing',
  COMPETITOR: 'Competitor',
  INTERNAL: 'Internal',
  OTHER: 'Other',
};

const categoryColors: Record<CloseReasonCategory, 'default' | 'warning' | 'error' | 'info' | 'primary'> = {
  NO_RESPONSE: 'default',
  NOT_A_FIT: 'warning',
  BUDGET: 'error',
  TIMING: 'info',
  COMPETITOR: 'primary',
  INTERNAL: 'default',
  OTHER: 'default',
};

export default function CloseReasonSettings() {
  const { data: reasons, isLoading } = useCloseReasons();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CloseReason | null>(null);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Close Reasons"
          subtitle="Configure reasons for closing deals. These are presented when a deal is closed without a win."
          actions={
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Reason
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {(reasons ?? []).map((reason) => (
            <CloseReasonRow
              key={reason.id}
              reason={reason}
              onEdit={() => setEditing(reason)}
            />
          ))}
        </CardBody>
      </Card>

      {showCreate && (
        <CreateCloseReasonDialog open={showCreate} onOpenChange={setShowCreate} />
      )}

      {editing && (
        <EditCloseReasonDialog
          reason={editing}
          open={!!editing}
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </div>
  );
}

function CloseReasonRow({
  reason,
  onEdit,
}: {
  reason: CloseReason;
  onEdit: () => void;
}) {
  const deleteReason = useDeleteCloseReason();

  const handleDelete = async () => {
    if (!confirm(`Delete "${reason.label}"?`)) return;
    await deleteReason.mutateAsync(reason.id);
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-grey-50 group">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-secondary">{reason.label}</span>
          <Badge variant={categoryColors[reason.category]} size="sm">
            {categoryLabels[reason.category]}
          </Badge>
          {reason.isSystem && (
            <Badge variant="outline" size="sm">System</Badge>
          )}
        </div>
        {reason.description && (
          <p className="text-xs text-grey-500 mt-0.5">{reason.description}</p>
        )}
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
          aria-label={`Edit ${reason.label}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleDelete}
          className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
          aria-label={`Delete ${reason.label}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function CreateCloseReasonDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    label: '',
    description: '',
    category: 'OTHER' as CloseReasonCategory,
  });
  const createReason = useCreateCloseReason();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createReason.mutateAsync({
      label: form.label,
      description: form.description || undefined,
      category: form.category,
      position: 99,
    });
    onOpenChange(false);
    setForm({ label: '', description: '', category: 'OTHER' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Close Reason"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-close-reason-form" loading={createReason.isPending}>
            Add Reason
          </Button>
        </>
      }
    >
      <form id="create-close-reason-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Label"
          required
          placeholder="e.g. Budget Constraints"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <Select
          label="Category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value as CloseReasonCategory })}
          options={Object.entries(categoryLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Textarea
          label="Description"
          placeholder="Brief explanation of this close reason..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </form>
    </Modal>
  );
}

function EditCloseReasonDialog({
  reason,
  open,
  onOpenChange,
}: {
  reason: CloseReason;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    label: reason.label,
    description: reason.description || '',
    category: reason.category,
  });
  const updateReason = useUpdateCloseReason();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateReason.mutateAsync({
      id: reason.id,
      data: {
        label: form.label,
        description: form.description || undefined,
        category: form.category,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Close Reason"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="edit-close-reason-form" loading={updateReason.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-close-reason-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Label"
          required
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <Select
          label="Category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value as CloseReasonCategory })}
          options={Object.entries(categoryLabels).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </form>
    </Modal>
  );
}
